import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Users, Shield, Anchor, Check, AlertCircle, CheckCircle2, Trophy, Activity, Globe } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { useUser } from '../context/UserContext';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

export default function SyndicatePage() {
  const [loadingCode, setLoadingCode] = useState('');
  const { isMember } = useUser();

  const navigate = useNavigate();

  const handleCheckout = async (tierName: string, price: number, tierId: string) => {
    navigate(`/membership-checkout?planId=${tierId}`);
  };

  const tiers = [
    {
      id: 'prospect',
      name: 'Prospect',
      price: 'FREE',
      period: 'No Ongoing Commitment',
      description: 'The starting point. Access the OWS booking platform and book sessions at standard rates.',
      icon: Users,
      features: [
        'Pay-As-You-Go for All Training',
        'Standard Rate for Expeditions',
        'Official OWS Portal Access',
        'Basic Performance Tracking',
        'Eligible for Rank Advancement'
      ],
      buttonText: 'Register as Prospect',
      buttonAction: () => handleCheckout('Prospect', 0, 'prospect-tier'),
      highlighted: false,
      buttonClass: 'bg-white text-ows-dark border border-gray-200 hover:border-ows-teal hover:text-ows-teal',
    },
    {
      id: 'syndicate',
      name: 'Syndicate Member',
      price: '$300',
      period: 'Per Season (June to September)',
      description: 'The primary OWS membership. Seasonal access to advanced training, group expeditions, and priority booking.',
      icon: Shield,
      features: [
        'Free Members Group Trips (Weekly Saturday & Sunday)',
        '50% Off Private Training',
        'Official Syndicate Membership & Rank',
        'Instructor-Led Safety Sessions',
        'Endurance Progression Tracking'
      ],
      buttonText: 'Join the Syndicate',
      buttonAction: () => handleCheckout('Syndicate Member', 300, 'syndicate-ultimate'),
      highlighted: true,
      buttonClass: 'bg-ows-teal text-white hover:bg-white hover:text-ows-dark',
    },
    {
      id: 'elite',
      name: 'Elite Status',
      price: 'MERIT ONLY',
      period: 'Post-Crossing Achievement',
      description: (
        <>
          Status can only be achieved after successfully crossing Lake Ontario. No money can buy it. This status grants you exclusive clearance to book Elite Expeditions.{' '}
          <a href="https://cert.openwatersyndicate.com" target="_blank" rel="noopener noreferrer" className="block mt-4 text-ows-teal font-bold uppercase tracking-widest text-[10px] hover:text-ows-dark transition-colors">
            Requirement to pass the test: cert.openwatersyndicate.com
          </a>
        </>
      ),
      icon: Trophy,
      features: [
        '100% Free Training & Group Coaching',
        'Requires Lake Ontario Crossing Completion',
        'Cannot Be Bought',
        'Unlocks Elite Expeditions Booking',
        'Exclusive Hardware Prototyping'
      ],
      buttonText: 'Track Evolution Progress',
      buttonAction: () => navigate('/member-portal'),
      highlighted: false,
      buttonClass: 'bg-[#141414] text-white hover:bg-ows-teal',
    }
  ];

  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Syndicate Membership | Join the Alpha Cohort | OWS</title>
        <meta name="description" content="Secure your position in the Syndicate Alpha Cohort. Elite maritime access, mission-ready gear, and coordinates to the world's most remote waters." />
        <meta name="keywords" content="OWS Syndicate, Alpha Cohort, maritime membership, expedition club, private paddleboarding group" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">
            Join the Syndicate
          </h1>
          <p className="text-lg text-ows-dark/60 max-w-2xl mx-auto font-light leading-relaxed mb-8">
            We are a community of paddlers training toward open water distance. Choose your entry point.
          </p>
          <button onClick={() => navigate('/membership-requirements')} className="inline-flex items-center gap-2 px-6 py-3 bg-ows-teal text-white rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-ows-dark transition-all">
            <Shield className="h-4 w-4" /> Membership Requirements
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-32">
          {tiers.map((tier, index) => {
            const Icon = tier.icon;
            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex flex-col p-10 rounded-[3rem] transition-all duration-300 ${tier.highlighted ? 'bg-ows-dark text-white shadow-2xl scale-105 z-10 border border-white/10' : 'bg-white text-ows-dark border border-ows-teal/10 hover:shadow-xl hover:border-ows-teal/30'}`}
              >
                {tier.highlighted && (
                  <div className="absolute top-0 right-0 w-48 h-48 bg-ows-teal/20 rounded-full blur-3xl -mr-24 -mt-24 pointer-events-none"></div>
                )}

                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${tier.highlighted ? 'bg-ows-teal text-white' : 'bg-ows-sand text-ows-dark'}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold uppercase tracking-widest">{tier.name}</h2>
                  </div>
                </div>

                <div className="mb-6 relative z-10">
                  <div className="flex items-end gap-2">
                    <span className={`text-4xl font-display uppercase tracking-widest ${tier.highlighted ? 'text-ows-teal' : 'text-ows-dark'}`}>{tier.price}</span>
                    <span className={`text-xs uppercase tracking-widest font-bold mb-1.5 ${tier.highlighted ? 'text-white/40' : 'text-ows-dark/40'}`}>/ {tier.period}</span>
                  </div>

                  <p className={`mt-4 text-sm font-light leading-relaxed ${tier.highlighted ? 'text-white/60' : 'text-ows-dark/60'}`}>
                    {tier.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-grow relative z-10">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className={`h-4 w-4 shrink-0 mt-0.5 ${tier.highlighted ? 'text-ows-teal' : 'text-ows-teal/60'}`} />
                      <span className={`text-sm ${tier.highlighted ? 'text-white/80' : 'text-ows-dark/80'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isMember ? (
                  <button
                    onClick={() => navigate('/member-portal')}
                    className={`w-full py-4 font-bold tracking-[0.2em] uppercase text-xs transition-all rounded-full flex items-center justify-center gap-2 relative z-10 bg-ows-teal text-white hover:bg-ows-dark`}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Operator Active - Enter Portal
                    <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={tier.buttonAction}
                    disabled={loadingCode === tier.name}
                    className={`w-full py-4 font-bold tracking-[0.2em] uppercase text-xs transition-all rounded-full flex items-center justify-center gap-2 relative z-10 disabled:opacity-50 ${tier.buttonClass}`}
                  >
                    {loadingCode === tier.name ? 'Processing...' : tier.buttonText}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* GLOBAL MISSION TRACKING TEASER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto mb-32 relative overflow-hidden rounded-[3rem] bg-ows-dark p-12 md:p-20 border border-white/10"
        >
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')] bg-cover bg-center opacity-20 hover:scale-105 transition-transform duration-[10s] ease-linear pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-ows-dark via-ows-dark/80 to-transparent"></div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-display font-light text-white uppercase tracking-widest mb-8 leading-tight">
                TRACK <br /><span className="text-ows-teal">YOUR EXPEDITION</span>
              </h2>
              <p className="text-white/60 text-lg font-light leading-relaxed mb-10 max-w-lg">
                Every OWS expedition is tracked in real-time using GPS. View active routes, past expeditions, and OWS paddling zones on the map.
              </p>
              <Link
                to="/mission-map"
                className="inline-flex items-center gap-4 px-10 py-5 bg-ows-teal text-white rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-ows-dark transition-all shadow-2xl shadow-ows-teal/20"
              >
                View Expedition Map <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="relative aspect-square lg:aspect-video rounded-[2rem] overflow-hidden border border-white/10 bg-ows-dark/40 backdrop-blur-sm group">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Globe className="h-16 w-16 text-ows-teal/40 mb-6 mx-auto animate-pulse" />
                  <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.4em]">Establishing Satellite Link...</p>
                </div>
              </div>
              {/* Visual purely for aesthetic teaser */}
              <div className="absolute inset-0 bg-gradient-to-br from-ows-teal/20 to-transparent mix-blend-overlay"></div>
              <div className="absolute top-8 left-8 p-4 bg-white/5 border border-white/10 rounded-xl backdrop-blur-md">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-ping"></div>
                  <span className="text-[8px] font-mono text-white uppercase tracking-widest">Tracking Operator 042</span>
                </div>
                <div className="h-1 w-24 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: "0%" }}
                    whileInView={{ width: "70%" }}
                    transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                    className="h-full bg-ows-teal"
                  ></motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-20 max-w-3xl mx-auto flex items-start gap-4 p-8 bg-ows-sand/30 rounded-[2rem] border border-ows-teal/10"
        >
          <Shield className="h-6 w-6 text-ows-teal shrink-0" />
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-ows-dark mb-2">Community First</h4>
            <p className="text-sm text-ows-dark/60 font-light leading-relaxed">
              Open Water Syndicate is not a gear marketplace. We are a community of practitioners dedicated to open-water resilience. While we provide the hardware necessary for our Syndicate members, our focus remains on the training, protocols, and brotherhood of exploration.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
